/**
 * EquipmentTypeChange Component
 *
 * Allows users to change the type of an equipment entity.
 * Warns users if the type change will result in data loss (fields that exist
 * in the current type but not in the new type).
 */
import { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Button } from 'react-bootstrap';
import { Autocomplete } from '@mui/material';
import TextField from '@mui/material/TextField';
import Form from 'react-bootstrap/Form';
import { Api } from './interface';
import { toast } from 'react-toastify';
import { fetchTemplate } from './common';
import Checkbox from '@mui/material/Checkbox';
import moment from 'moment';

export default function EquipmentTypeChange(props) {
  const [types, setTypes] = useState([]);
  const [newEntityType, setNewEntityType] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState([]);

  // State to store fields that will be lost during the type change
  const [templateDiff, setTemplateDiff] = useState([]);
  const [displayTemplateDiff, setDisplayTemplateDiff] = useState(false);

  // State to track if user has confirmed they understand data will be lost
  const [confirmTypeChange, setConfirmTypeChange] = useState(false);
  const [currentEntityType, setCurrentEntityType] = useState({
    EntityType: 'No Type Selected',
    EntityType_DBID: 0,
  });

  /**
   * Effect: Initialize component data on mount
   * Fetches available equipment types and sets the current type
   */
  useEffect(() => {
    Api({
      sp: 'getEntityTypesByLevel',
      json: { level: props.level },
    }).then((response) => {
      setTypes(response);
      const type = response.filter(
        (x) => x.EntityType === props.items[0].EntityType
      )[0];

      setCurrentEntityType(type);
      fetchCurrentTemplate(type);
    });
  }, []);

  /**
   * Fetches and stores the metadata template for the current equipment type
   * @param {Object} type - The equipment type object
   */
  const fetchCurrentTemplate = async (type) => {
    let template = await fetchTemplate(type);
    console.log('Current template:', template);
    setCurrentTemplate(template);
  };

  /**
   * Fetches the template for a new type and calculates which fields will be lost
   * @param {Object} type - The new equipment type object
   */
  const fetchNewTemplate = async (type) => {
    let template = await fetchTemplate(type);

    // Find fields that exist in current template but NOT in new template
    // These are the fields that will be permanently deleted
    const missingEntries = currentTemplate.filter(
      (currentItem) =>
        !template.some(
          (newItem) => newItem.EntityMetaType === currentItem.EntityMetaType
        )
    );

    setTemplateDiff(missingEntries);
    setDisplayTemplateDiff(missingEntries.length > 0);
  };

  /**
   * Performs the equipment type update via API call
   * @param {Object} newValue - The new equipment type object
   */
  const updateType = (newValue) => {
    // Prepare metadata that will be deleted - include current values for history
    const templateDiffData = templateDiff.map((entry) => ({
      ...entry,
      Entity_DBID: props.items[0].Entity_DBID,
      EntityMetaValue: props.items[0][entry.EntityMetaType] || null,
    }));

    Api({
      sp: 'updateEquipmentType',
      json: {
        Entity_DBID: props.items[0].Entity_DBID,
        newType: newValue.EntityType_DBID,
        deleteMeta: templateDiffData,
      },
    }).then((response) => {
      if (response[0].error) {
        toast.error(response[0].error);
      } else {
        toast.success(
          'Successfully updated type for ' +
            props.items[0].EntityName +
            ' to ' +
            newValue.EntityType
        );
        props.close(true);
      }
    });
  };

  /**
   * Validates whether the confirm button should be disabled
   * @returns {boolean} true if button should be disabled, false otherwise
   *
   * Button is disabled when:
   * 1. User hasn't confirmed AND new type differs from current type
   * 2. No new type has been selected
   * 3. Selected type is the same as current type
   */
  const validateTypeChange = () => {
    return (
      (!confirmTypeChange &&
        newEntityType?.EntityType_DBID !== currentEntityType.EntityType_DBID) ||
      !newEntityType ||
      newEntityType?.EntityType_DBID === currentEntityType.EntityType_DBID
    );
  };

  return props.items.length && types.length ? (
    <Modal
      size="lg"
      show={props.open}
      onHide={() => props.close()}
      className="entity-modal"
    >
      <Modal.Header closeButton="true">
        <Modal.Title>Change Type For {props.items[0].EntityName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Dropdown to select new equipment type */}
          <Autocomplete
            disablePortal
            disableClearable
            id="combo-box-demo"
            options={types}
            getOptionLabel={(option) => option.EntityType}
            sx={{ width: '100%' }}
            defaultValue={currentEntityType}
            onChange={(event, newValue) => {
              setNewEntityType(newValue);
              // When user selects a type, fetch its template
              fetchNewTemplate(newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Equipment Type" />
            )}
          />
        </Form>

        {/* Warning section - only shown if fields will be lost */}
        {displayTemplateDiff ? (
          <div className="template-diff-warning">
            <p>
              Warning: Changing equipment type from{' '}
              {currentEntityType.EntityType} to {newEntityType.EntityType} will
              remove the following fields permanently:
            </p>

            {/* List each field that will be deleted with its current value */}
            {templateDiff.map((entry, index) => {
              // Format the field value
              let EntityMetaValue = moment(
                props.items[0][entry.EntityMetaType]
              ).isValid()
                ? new Date(
                    props.items[0][entry.EntityMetaType]
                  ).toLocaleDateString()
                : props.items[0][entry.EntityMetaType];

              return (
                <span key={index} className="template-diff-warning-item">
                  <span className="template-diff-warning-idx">
                    {index + 1}.
                  </span>
                  {entry.EntityMetaType}
                  <em>
                    {/* Show current value if it exists */}
                    {props.items[0][entry.EntityMetaType]
                      ? ' - ' + EntityMetaValue
                      : ' '}
                  </em>
                </span>
              );
            })}

            {/* Checkbox requiring user to confirm they understand data loss */}
            <span className="template-diff-warning-confirm">
              Please select to confirm{' '}
              <Checkbox
                checked={confirmTypeChange}
                onChange={(event) => {
                  setConfirmTypeChange(event.target.checked);
                }}
              />
            </span>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <div className="outer">
          <div className="confirm">
            <>
              <Button
                disabled={validateTypeChange()}
                variant="outline-primary"
                onClick={() => updateType(newEntityType)}
              >
                Confirm
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => props.close(true)}
              >
                Cancel
              </Button>
            </>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  ) : (
    <></>
  );
}
